import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import { formatarCentavos } from '@/pages/loja/utils';
import FolhaReimprimir from './FolhaReimprimir';
import ModalDesfazer from './ModalDesfazer';
import type { VendaRecente } from '../types';

interface Props {
  eventoId: number;
  /** Direção pode desfazer venda da maquininha (estorno). Operador comum, não. */
  direcao: boolean;
  /** Caixa imprime fichas: habilita a reimpressão. */
  imprime: boolean;
  aoFechar: () => void;
  aoDesfeito: () => void;
}

const ROTULO_METODO: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'Pix',
  cartao: 'Cartão',
};

const ICONE_METODO: Record<string, string> = {
  dinheiro: 'ri-money-dollar-circle-line',
  pix: 'ri-qr-code-line',
  cartao: 'ri-bank-card-line',
};

function textoImpressoes(venda: VendaRecente): string | null {
  const imp = venda.impressoes;
  if (!imp) return null;
  const partes: string[] = [];
  if (imp.impressas) partes.push(`${imp.impressas} impressa${imp.impressas === 1 ? '' : 's'}`);
  if (imp.sem_imprimir) partes.push(`${imp.sem_imprimir} sem imprimir`);
  return partes.length > 0 ? partes.join(' · ') : null;
}

// Folha com as últimas vendas, reimpressão de fichas e "Desfazer".
export default function ModalRecentes({ eventoId, direcao, imprime, aoFechar, aoDesfeito }: Props) {
  const [vendas, setVendas] = useState<VendaRecente[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reimprimirPedido, setReimprimirPedido] = useState<number | null>(null);
  const [desfazerPedido, setDesfazerPedido] = useState<number | null>(null);

  async function carregar() {
    const { data, error } = await supabase.rpc('caixa_vendas_recentes', {
      p_evento: eventoId,
      p_limite: 20,
    });
    if (error) throw error;
    setVendas((data as VendaRecente[]) || []);
  }

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('caixa_vendas_recentes', {
          p_evento: eventoId,
          p_limite: 20,
        });
        if (error) throw error;
        if (ativo) setVendas((data as VendaRecente[]) || []);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} aria-hidden />
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-[480px] flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
          <h2 className="text-lg font-bold text-foreground-950">Últimas vendas</h2>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {carregando ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-xl bg-secondary-100" />
              ))}
            </div>
          ) : erro ? (
            <p className="text-sm text-danger-600">{erro}</p>
          ) : vendas.length === 0 ? (
            <p className="py-6 text-center text-sm text-secondary-500">Nenhuma venda ainda.</p>
          ) : (
            <ul className="space-y-2">
              {vendas.map((v) => {
                const hora = v.em ? new Date(v.em) : null;
                const horaTexto = hora
                  ? hora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                  : '';
                const impressoes = textoImpressoes(v);
                const podeReimprimir = imprime && (v.impressoes?.impressas ?? 0) >= 0 && !!v.impressoes;
                const podeDesfazer = v.pode_desfazer || direcao;
                const ehMaquininha = v.metodo === 'cartao' || v.metodo === 'pix';
                return (
                  <li key={v.pedido_id} className="rounded-xl border border-secondary-200 px-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                        <i className={`${ICONE_METODO[v.metodo] ?? 'ri-receipt-line'} text-lg`} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-secondary-500">
                            #{v.numero} · {horaTexto}
                          </span>
                          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-700">
                            {ROTULO_METODO[v.metodo] ?? v.metodo}
                          </span>
                        </div>
                        <p className="truncate text-[15px] font-medium text-foreground-950">
                          {v.resumo}
                        </p>
                        {impressoes && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs text-secondary-500">
                            <span className="flex h-3.5 w-3.5 items-center justify-center">
                              <i className="ri-printer-line" />
                            </span>
                            {impressoes}
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 text-base font-bold text-foreground-950">
                        {formatarCentavos(v.total_centavos)}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                      {podeReimprimir && (
                        <button
                          type="button"
                          onClick={() => setReimprimirPedido(v.pedido_id)}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-2.5 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                        >
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className="ri-printer-line" />
                          </span>
                          Reimprimir
                        </button>
                      )}
                      {podeDesfazer ? (
                        <button
                          type="button"
                          onClick={() => setDesfazerPedido(v.pedido_id)}
                          className="cursor-pointer whitespace-nowrap rounded-lg border border-danger-200 bg-white px-2.5 py-1.5 text-sm font-medium text-danger-600 transition-colors hover:bg-danger-50"
                        >
                          Desfazer
                        </button>
                      ) : ehMaquininha && !direcao ? (
                        <p className="text-right text-xs text-secondary-500">
                          Venda na maquininha: peça à direção para desfazer.
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <FolhaReimprimir
        aberto={reimprimirPedido != null}
        pedidoId={reimprimirPedido}
        aoFechar={() => setReimprimirPedido(null)}
        aoFim={(mensagem) => mostrarToast(mensagem, 4000, 'sucesso')}
      />

      <ModalDesfazer
        aberto={desfazerPedido != null}
        pedidoId={desfazerPedido}
        direcao={direcao}
        aoFechar={() => setDesfazerPedido(null)}
        aoDesfeito={(mensagem) => {
          mostrarToast(mensagem, 4000, 'sucesso');
          aoDesfeito();
          void carregar();
        }}
      />
    </div>
  );
}